"use client";

import createGlobe from "cobe";
import { useEffect, useId, useRef } from "react";

import { cn } from "@workspace/ui";

import { useHydrated } from "~/lib/hooks/use-hydrated";

import { EDGE_GLOBE_NODES, EDGE_GLOBE_ROUTES } from "../globe/edge-globe-data";
import {
  getArcControl,
  getArcPosition,
  getGlobePosition,
  GLOBE_PHI,
  GLOBE_ROLL,
  MARKER_ELEVATION,
  projectGlobePosition,
} from "../globe/edge-globe-projection";
import styles from "./edge-globe.module.css";

import type { Arc, Marker } from "cobe";

const DEVICE_PIXEL_RATIO = 2;
const DRAG_ROTATION = Math.PI * 2;
const DRAG_SMOOTHING = 20;
const AUTO_ROTATION_SPEED = (Math.PI * 2) / 160;
const ARC_HEIGHT = 0.3;
const ARC_WIDTH = 0.5;
const DEPLOY_COMMAND = "wrangler deploy";
const DEPLOY_LABEL_WIDTH = DEPLOY_COMMAND.length * 5.7 + 24;
const THUMBNAIL_WIDTH = 136;
const THUMBNAIL_HEIGHT = 102;
const THUMBNAIL_PADDING = 2.5;
const THUMBNAIL_FRAME_WIDTH = THUMBNAIL_WIDTH + THUMBNAIL_PADDING * 2;
const THUMBNAIL_FRAME_HEIGHT = THUMBNAIL_HEIGHT + THUMBNAIL_PADDING * 2;
const THUMBNAIL_FRAME_BOTTOM = -1;
const THUMBNAIL_IMAGE_BOTTOM = THUMBNAIL_FRAME_BOTTOM - THUMBNAIL_PADDING;
const THUMBNAIL_IMAGE_Y = THUMBNAIL_IMAGE_BOTTOM - THUMBNAIL_HEIGHT;
const THUMBNAIL_FRAME_Y = THUMBNAIL_FRAME_BOTTOM - THUMBNAIL_FRAME_HEIGHT;
const THUMBNAIL_CENTER_Y = THUMBNAIL_IMAGE_BOTTOM - THUMBNAIL_HEIGHT / 2;

interface EdgeGlobeProps {
  readonly className?: string;
}

export const EdgeGlobe = ({ className }: EdgeGlobeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<SVGSVGElement>(null);
  const thumbnailClipId = useId();
  const hydrated = useHydrated();

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) {
      return;
    }

    const colorCanvas = document.createElement("canvas");
    colorCanvas.width = 1;
    colorCanvas.height = 1;
    const colorContext = colorCanvas.getContext("2d", {
      willReadFrequently: true,
    });
    if (!colorContext) {
      return;
    }

    const getThemeColor = (
      variable: "--primary" | "--border" | "--background",
    ): [number, number, number] => {
      const color = getComputedStyle(canvas).getPropertyValue(variable);
      colorContext.fillStyle = `hsl(${color})`;
      colorContext.fillRect(0, 0, 1, 1);
      const [red = 0, green = 0, blue = 0] = colorContext.getImageData(
        0,
        0,
        1,
        1,
      ).data;
      return [red / 255, green / 255, blue / 255];
    };

    const getArcColor = (): [number, number, number] => {
      const primary = getThemeColor("--primary");
      const background = getThemeColor("--background");
      return [
        primary[0] * 0.75 + background[0] * 0.25,
        primary[1] * 0.75 + background[1] * 0.25,
        primary[2] * 0.75 + background[2] * 0.25,
      ];
    };

    const overlay = overlayRef.current;
    const nodePositions = new Map(
      EDGE_GLOBE_NODES.map((node) => [
        node.id,
        getGlobePosition(node.location),
      ]),
    );
    const arcs: Arc[] = [];
    const markers: Marker[] = EDGE_GLOBE_NODES.filter(
      (node) => node.id === "deploy",
    ).map((node) => ({ location: [...node.location], size: 0.03 }));
    const routes = EDGE_GLOBE_ROUTES.flatMap((route, index) => {
      const from = EDGE_GLOBE_NODES.find((node) => node.id === route.from);
      const to = EDGE_GLOBE_NODES.find((node) => node.id === route.to);
      const fromPosition = nodePositions.get(route.from);
      const toPosition = nodePositions.get(route.to);
      if (!from || !to || !fromPosition || !toPosition) {
        return [];
      }
      // Reverse directions share one curve, with packets travelling both ways.
      if (
        !EDGE_GLOBE_ROUTES.slice(0, index).some(
          (previous) =>
            previous.from === route.to && previous.to === route.from,
        )
      ) {
        arcs.push({ from: [...from.location], to: [...to.location] });
      }
      return [
        {
          ...route,
          fromPosition,
          toPosition,
          control: getArcControl(fromPosition, toPosition, ARC_HEIGHT),
          element: overlay?.querySelector<SVGGElement>(
            `[data-route="${index}"]`,
          ),
        },
      ];
    });
    const nodeElements = EDGE_GLOBE_NODES.map((node) => ({
      isDeployment: node.id === "deploy",
      position: getGlobePosition(node.location),
      element: overlay?.querySelector<SVGGElement>(`[data-node="${node.id}"]`),
      label: overlay?.querySelector<SVGGElement>(
        `[data-node="${node.id}"] [data-deploy-label]`,
      ),
    }));
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let isVisible = true;
    let motionTime = 0;
    const isDark = document.documentElement.classList.contains("dark");
    let size = canvas.clientWidth;
    overlay?.setAttribute("viewBox", `0 0 ${size} ${size}`);
    let autoYaw = -1.6;
    let dragYaw = 0;
    let renderedDragYaw = 0;
    let activePointerId: number | null = null;
    let lastPointerX = 0;

    const globe = createGlobe(canvas, {
      devicePixelRatio: DEVICE_PIXEL_RATIO,
      width: size,
      height: size,
      phi: GLOBE_PHI,
      theta: 0,
      roll: GLOBE_ROLL,
      yaw: autoYaw + renderedDragYaw,
      dark: isDark ? 1 : 0,
      diffuse: 1.2,
      mapSamples: 32000,
      mapDotSize: 0.75,
      mapBrightness: 6,
      baseColor: [1, 1, 1],
      mapColor: getThemeColor("--primary"),
      mapEdgeFade: 0,
      surfaceOpacity: 0,
      backfaceOpacity: 0.25,
      gridColor: getThemeColor("--border"),
      gridOpacity: 1,
      gridWidth: 1,
      gridLongitudeStep: 20,
      markerColor: getThemeColor("--primary"),
      markerElevation: MARKER_ELEVATION,
      markers,
      arcs,
      arcColor: getArcColor(),
      arcHeight: ARC_HEIGHT,
      arcWidth: ARC_WIDTH,
      glowColor: [0, 0, 0],
    });

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) {
        return;
      }

      const width = entry.contentRect.width;
      size = width;
      overlay?.setAttribute("viewBox", `0 0 ${width} ${width}`);
      globe.update({ width, height: width });
    });
    resizeObserver.observe(canvas);

    const themeObserver = new MutationObserver(() => {
      globe.update({
        dark: document.documentElement.classList.contains("dark") ? 1 : 0,
        mapColor: getThemeColor("--primary"),
        gridColor: getThemeColor("--border"),
        markerColor: getThemeColor("--primary"),
        arcColor: getArcColor(),
      });
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme", "style"],
    });

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry?.isIntersecting ?? false;
      },
      { rootMargin: "100px" },
    );
    visibilityObserver.observe(container);

    const startDrag = (event: PointerEvent) => {
      if (!event.isPrimary || event.button !== 0 || activePointerId !== null) {
        return;
      }

      activePointerId = event.pointerId;
      lastPointerX = event.clientX;
      container.setPointerCapture(event.pointerId);
      container.dataset.dragging = "true";
    };

    const drag = (event: PointerEvent) => {
      if (event.pointerId !== activePointerId) {
        return;
      }

      dragYaw +=
        ((event.clientX - lastPointerX) / container.clientWidth) *
        DRAG_ROTATION;
      lastPointerX = event.clientX;
    };

    const finishDrag = (pointerId: number) => {
      if (pointerId !== activePointerId) {
        return;
      }

      activePointerId = null;
      delete container.dataset.dragging;
      if (container.hasPointerCapture(pointerId)) {
        container.releasePointerCapture(pointerId);
      }
    };

    const endDrag = (event: PointerEvent) => {
      finishDrag(event.pointerId);
    };

    container.addEventListener("pointerdown", startDrag);
    container.addEventListener("pointermove", drag);
    container.addEventListener("pointerup", endDrag);
    container.addEventListener("pointercancel", endDrag);
    container.addEventListener("lostpointercapture", endDrag);

    let frameId = 0;
    let lastFrameTime = performance.now();

    const animate = (time: number) => {
      const elapsed = Math.min((time - lastFrameTime) / 1000, 0.1);
      lastFrameTime = time;
      if (!isVisible) {
        frameId = requestAnimationFrame(animate);
        return;
      }
      // Approach the drag target without momentum or overshoot.
      renderedDragYaw +=
        (dragYaw - renderedDragYaw) * (1 - Math.exp(-DRAG_SMOOTHING * elapsed));
      if (Math.abs(dragYaw - renderedDragYaw) < 0.0001) {
        renderedDragYaw = dragYaw;
      }
      if (activePointerId === null && !reducedMotion.matches) {
        autoYaw += elapsed * AUTO_ROTATION_SPEED;
      }
      const yaw = autoYaw + renderedDragYaw;
      globe.update({ yaw });

      motionTime += reducedMotion.matches ? 0 : elapsed;
      const thumbnailScale = Math.max(1, Math.min(1.25, size / 600));
      for (const { isDeployment, position, element, label } of nodeElements) {
        const projected = projectGlobePosition(position, yaw, size);
        element?.setAttribute(
          "transform",
          `translate(${projected.x} ${projected.y}) scale(${isDeployment ? 1 : thumbnailScale})`,
        );
        element?.setAttribute(
          "data-visible",
          projected.visible ? "true" : "false",
        );
        if (isDeployment) {
          const halfWidth = DEPLOY_LABEL_WIDTH / 2;
          const labelX = Math.min(
            Math.max(projected.x, halfWidth + 8),
            size - halfWidth - 8,
          );
          label?.setAttribute(
            "transform",
            `translate(${labelX - projected.x} 0)`,
          );
        }
      }
      for (const route of routes) {
        const progress = reducedMotion.matches
          ? 0.5
          : ((motionTime + route.delay) % route.duration) / route.duration;
        const position = getArcPosition(
          route.fromPosition,
          route.control,
          route.toPosition,
          progress,
        );
        const projected = projectGlobePosition(position, yaw, size);
        route.element?.setAttribute(
          "transform",
          `translate(${projected.x} ${projected.y})`,
        );
        route.element?.setAttribute("opacity", projected.visible ? "1" : "0");
      }
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frameId);
      container.removeEventListener("pointerdown", startDrag);
      container.removeEventListener("pointermove", drag);
      container.removeEventListener("pointerup", endDrag);
      container.removeEventListener("pointercancel", endDrag);
      container.removeEventListener("lostpointercapture", endDrag);
      if (activePointerId !== null) {
        finishDrag(activePointerId);
      }
      resizeObserver.disconnect();
      themeObserver.disconnect();
      visibilityObserver.disconnect();
      globe.destroy();
    };
  }, [hydrated]);

  return (
    <div
      className={cn(
        "-mx-6 w-[calc(100%+3rem)] overflow-x-clip md:-mt-8 lg:-mt-12 xl:-mt-16",
        className,
      )}
    >
      <div className="relative left-1/2 aspect-2/1 w-[180%] -translate-x-1/2 overflow-hidden sm:w-[110%]">
        <div
          ref={containerRef}
          className="absolute top-0 left-0 aspect-square w-full cursor-grab touch-pan-y overflow-clip select-none data-[dragging=true]:cursor-grabbing"
          aria-hidden
        >
          <canvas ref={canvasRef} className="pointer-events-none size-full" />
          <svg
            ref={overlayRef}
            className="pointer-events-none absolute inset-0 size-full overflow-visible"
            viewBox="0 0 1000 1000"
          >
            <defs>
              <clipPath id={thumbnailClipId}>
                <rect
                  x={-THUMBNAIL_WIDTH / 2}
                  y={THUMBNAIL_IMAGE_Y}
                  width={THUMBNAIL_WIDTH}
                  height={THUMBNAIL_HEIGHT}
                  rx="2"
                />
              </clipPath>
            </defs>
            {EDGE_GLOBE_ROUTES.map((route, index) => (
              <g
                key={`${route.from}-${route.to}`}
                data-route={index}
                opacity="0"
              >
                <circle r="6.5" className="fill-background/80" />
                <circle r="4" className="fill-primary" />
              </g>
            ))}
            {EDGE_GLOBE_NODES.map((node, index) => (
              <g
                key={node.id}
                data-node={node.id}
                data-visible="false"
                className={styles.node}
              >
                {node.id === "deploy" ? (
                  <g className="md:scale-125">
                    <circle
                      r="7"
                      className={cn(
                        "stroke-primary/70 fill-none",
                        styles.pulse,
                      )}
                      strokeWidth="1"
                    />
                    <circle
                      r="7"
                      className="fill-background stroke-primary"
                      strokeWidth="2"
                    />
                    <circle r="3.15" className="fill-primary" />
                    <g data-deploy-label className="text-xs font-medium">
                      <rect
                        x={-DEPLOY_LABEL_WIDTH / 2}
                        y="14"
                        width={DEPLOY_LABEL_WIDTH}
                        height="27"
                        rx="7"
                        className="fill-secondary dark:fill-secondary-foreground"
                      />
                      <text
                        y="31"
                        textAnchor="middle"
                        className="fill-secondary-foreground dark:fill-secondary"
                      >
                        {DEPLOY_COMMAND}
                      </text>
                    </g>
                  </g>
                ) : (
                  <g
                    transform={`rotate(${index % 2 === 0 ? -6 : 6} 0 ${THUMBNAIL_CENTER_Y})`}
                    className="scale-55 filter-[drop-shadow(0_6px_8px_rgb(0_0_0/0.25))] sm:scale-70 md:scale-85 xl:scale-100"
                  >
                    <rect
                      x={-THUMBNAIL_FRAME_WIDTH / 2}
                      y={THUMBNAIL_FRAME_Y}
                      width={THUMBNAIL_FRAME_WIDTH}
                      height={THUMBNAIL_FRAME_HEIGHT}
                      rx="4"
                      className="fill-background"
                    />
                    <image
                      href="/images/landing/edge/demo/light.webp"
                      x={-THUMBNAIL_WIDTH / 2}
                      y={THUMBNAIL_IMAGE_Y}
                      width={THUMBNAIL_WIDTH}
                      height={THUMBNAIL_HEIGHT}
                      preserveAspectRatio="xMidYMid slice"
                      clipPath={`url(#${thumbnailClipId})`}
                      className="dark:hidden"
                    />
                    <image
                      href="/images/landing/edge/demo/dark.webp"
                      x={-THUMBNAIL_WIDTH / 2}
                      y={THUMBNAIL_IMAGE_Y}
                      width={THUMBNAIL_WIDTH}
                      height={THUMBNAIL_HEIGHT}
                      preserveAspectRatio="xMidYMid slice"
                      clipPath={`url(#${thumbnailClipId})`}
                      className="hidden dark:block"
                    />
                  </g>
                )}
              </g>
            ))}
          </svg>
        </div>

        <div className="to-background pointer-events-none absolute inset-x-0 bottom-0 z-10 h-1/2 bg-linear-to-b from-transparent md:h-1/3"></div>
      </div>
    </div>
  );
};
